import type { ReactNode } from 'react';
import { CONTROL_PANEL_PATHS, type FeatureState, type OperatorTenant } from '@forecastify/shared';
import { Button, Switch, cn } from '@forecastify/ui';
import { useT } from '../i18n';
import { BTN_SM, PILL, TH, TH_ROW } from '../chrome/tones';
import { SkeletonRows } from '../chrome/skeleton-rows';
import { featureName } from './feature-names';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Funkciók* — one row per registered flag, and where its value came from.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SOURCE PILL IS THE USEFUL HALF.** A switch showing *on* without saying whether that
 * came from the plan, an override or the shipped default is what an operator misreads at two in the
 * morning — and the three need different actions: change the plan, clear the override, or nothing.
 *
 * ⚠️ **"After clearing" is said BEFORE clearing**, from the server's own `withoutOverride`, so the
 * button never surprises anybody.
 */

type Write = (path: string, method: 'POST' | 'PUT' | 'DELETE', body?: unknown) => Promise<boolean>;

const SOURCE_TONE: Readonly<Record<FeatureState['source'], string>> = {
  override: 'bg-primary/10 text-primary',
  plan: 'bg-muted',
  default: 'text-muted-foreground border',
};

export function TenantFeatures({
  tenant,
  flags,
  busy,
  write,
}: {
  readonly tenant: OperatorTenant;
  readonly flags: readonly FeatureState[] | null;
  readonly busy: boolean;
  readonly write: Write;
}): ReactNode {
  const t = useT();

  const sourceLabel = (source: FeatureState['source']): string =>
    source === 'override'
      ? t('tenant.features.source.override')
      : source === 'plan'
        ? t('tenant.features.source.plan')
        : t('tenant.features.source.default');

  return (
    <section aria-label={t('tenant.features')} className="overflow-x-auto rounded-lg border">
      {flags === null ? (
        <p role="status" className="sr-only">
          {t('tenants.loading')}
        </p>
      ) : null}
      <table className="w-full min-w-[620px] text-[13px]">
        <thead className="bg-muted/40 border-b">
          <tr className={TH_ROW}>
            <th className={TH}>{t('tenant.features.column.name')}</th>
            <th className={TH}>{t('tenant.features.column.value')}</th>
            <th className={TH}>{t('tenant.features.column.source')}</th>
            <th className={TH} />
          </tr>
        </thead>
        <tbody>
          {flags === null ? (
            <SkeletonRows columns={4} rowClassName="h-11" />
          ) : (
            flags.map((flag) => (
              <tr key={flag.key} className="h-11 border-b last:border-0">
                <td className="px-3">
                  <span className="block font-medium">{featureName(flag.key, t)}</span>
                  <span className="text-muted-foreground block font-mono text-[11px]">
                    {flag.key}
                  </span>
                </td>
                <td className="px-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      label={featureName(flag.key, t)}
                      checked={flag.enabled}
                      disabled={busy}
                      onCheckedChange={(next) => {
                        void write(CONTROL_PANEL_PATHS.flag(tenant.id, flag.key), 'PUT', {
                          enabled: next,
                        });
                      }}
                    />
                    <span className="w-6 text-[12.5px]">
                      {flag.enabled ? t('feature.on') : t('feature.off')}
                    </span>
                  </div>
                </td>
                <td className="px-3">
                  <span className={cn(PILL, SOURCE_TONE[flag.source])}>
                    {sourceLabel(flag.source)}
                  </span>
                </td>
                <td className="px-3 text-right">
                  {flag.source === 'override' ? (
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-muted-foreground text-[12px] whitespace-nowrap">
                        {t('tenant.features.after', {
                          value: flag.withoutOverride ? t('feature.on') : t('feature.off'),
                        })}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={BTN_SM}
                        disabled={busy}
                        onClick={() => {
                          void write(CONTROL_PANEL_PATHS.flag(tenant.id, flag.key), 'DELETE');
                        }}
                      >
                        {t('tenant.features.clear')}
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
